import { PlatformInfo } from './platformUtils';

export interface ClipboardResult {
  success: boolean;
  data?: string;
  error?: string;
}

export const copyToClipboard = async (text: string, platform?: PlatformInfo): Promise<ClipboardResult> => {
  try {
    // Modern Clipboard API (preferred)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return { success: true };
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    
    if (platform?.isMobile) {
      // Mobile-specific selection
      textArea.contentEditable = 'true';
      textArea.readOnly = false;
      const range = document.createRange();
      range.selectNodeContents(textArea);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      textArea.setSelectionRange(0, 999999);
    } else {
      textArea.focus();
      textArea.select();
    }

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    return { success: successful };
  } catch (error) {
    if (!navigator.clipboard) {
      return { success: false, error: 'Clipboard API not available' };
    }
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to copy to clipboard'
    };
  }
};

export const readFromClipboard = async (platform?: PlatformInfo): Promise<ClipboardResult> => {
  try {
    // Modern Clipboard API (preferred)
    if (navigator.clipboard && navigator.clipboard.readText) {
      const text = await navigator.clipboard.readText();
      return { success: true, data: text };
    }

    // Fallback: Can't reliably read clipboard without modern API
    return { 
      success: false, 
      error: 'Clipboard read not supported. Please paste manually.' 
    };
  } catch (error) {
    if (!navigator.clipboard) {
      return { success: false, error: 'Clipboard API not available' };
    }
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to read from clipboard'
    };
  }
};

export const requestClipboardPermission = async (): Promise<boolean> => {
  try {
    if ('permissions' in navigator) {
      const permission = await navigator.permissions.query({ name: 'clipboard-read' as PermissionName });
      return permission.state === 'granted';
    }
    return true; // Assume permission if we can't check
  } catch {
    return true; // Fallback to true
  }
};